import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { EnemyShape } from '../../../core/types';

// Builds the solid each enemy renders as (shape encodes the digit — see
// EnemyShape in core/types). Every geometry honors the same contract so the
// renderer can treat them interchangeably: centered at origin, uniformly
// scaled to a bounding-sphere radius of NORMALIZED_RADIUS, flat-shaded for
// the polyhedra (non-indexed + recomputed normals) and smooth for the round
// shapes. Templates are cached per shape and SHARED across meshes: callers
// must never mutate or dispose them (userData.shared marks them so
// disposeObject skips them and ShapeShatter clones before mutating).

const NORMALIZED_RADIUS = 0.65;

// Base radius the polyhedra are built at before normalization. Only the
// proportions of each constructor call matter — normalization rescales.
const R = 0.65;

const templates = new Map<EnemyShape, THREE.BufferGeometry>();

export function createShapeGeometry(shape: EnemyShape): THREE.BufferGeometry {
  let template = templates.get(shape);
  if (!template) {
    template = buildTemplate(shape);
    template.userData.shared = true;
    templates.set(shape, template);
  }
  return template;
}

// Power-up star: a spiky stellated octahedron, smaller than enemies. Shared
// like every other template.
let starTemplate: THREE.BufferGeometry | null = null;

export function createStarGeometry(): THREE.BufferGeometry {
  if (!starTemplate) {
    starTemplate = normalize(flatten(buildStellatedOctahedron()));
    starTemplate.scale(0.55, 0.55, 0.55);
    starTemplate.userData.shared = true;
  }
  return starTemplate;
}

function buildTemplate(shape: EnemyShape): THREE.BufferGeometry {
  switch (shape) {
    case 'torus':
      return normalize(new THREE.TorusGeometry(R, R * 0.38, 16, 32));
    case 'cylinder':
      return normalize(new THREE.CylinderGeometry(R, R, R * 4.5, 24));
    case 'pizzaBox':
      return normalize(flatten(new THREE.BoxGeometry(1.1, 0.22, 1.1)));
    case 'tetrahedron':
      return normalize(flatten(new THREE.TetrahedronGeometry(R)));
    case 'cube':
      return normalize(flatten(new THREE.BoxGeometry(1, 1, 1)));
    case 'dodecahedron':
      return normalize(flatten(new THREE.DodecahedronGeometry(R)));
    case 'icosahedron':
      return normalize(flatten(new THREE.IcosahedronGeometry(R)));
    case 'octahedron':
      return normalize(flatten(new THREE.OctahedronGeometry(R)));
    case 'stellatedOctahedron':
      return normalize(flatten(buildStellatedOctahedron()));
    case 'stellatedDodecahedron':
      return normalize(flatten(buildStellatedDodecahedron()));
  }
}

// Enforces the shared contract: center on the origin, then uniformly scale so
// the bounding sphere has radius NORMALIZED_RADIUS. The renderer applies its
// own world scale on top of this canonical size.
function normalize(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  geometry.center();
  geometry.computeBoundingSphere();
  const radius = geometry.boundingSphere?.radius ?? 0;
  if (radius > 0) {
    const s = NORMALIZED_RADIUS / radius;
    geometry.scale(s, s, s);
    geometry.computeBoundingSphere();
  }
  return geometry;
}

// Gives a polyhedron the crisp faceted look: non-indexed vertices so no
// normals are shared across faces, then recomputed (flat) normals.
function flatten(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const nonIndexed = geometry.index ? geometry.toNonIndexed() : geometry;
  if (nonIndexed !== geometry) geometry.dispose();
  nonIndexed.computeVertexNormals();
  return nonIndexed;
}

// Strips a geometry down to positions only so differently-attributed pieces
// (e.g. a Tetrahedron with uvs and hand-built spikes without) can be merged;
// normals are recomputed after the merge by flatten().
function positionsOnly(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const bare = new THREE.BufferGeometry();
  const source = geometry.index ? geometry.toNonIndexed() : geometry;
  bare.setAttribute('position', source.getAttribute('position').clone());
  if (source !== geometry) source.dispose();
  geometry.dispose();
  return bare;
}

// Stella octangula: two tetrahedra, the second the point-reflection of the
// first. Negating (x,y,z) flips handedness (determinant -1), which turns
// every face inside-out — so the reflected copy swaps the 2nd and 3rd vertex
// of each triangle to restore outward winding before the merge.
function buildStellatedOctahedron(): THREE.BufferGeometry {
  const first = positionsOnly(new THREE.TetrahedronGeometry(R));

  const second = first.clone();
  const pos = second.getAttribute('position') as THREE.BufferAttribute;
  const arr = pos.array as Float32Array;
  for (let i = 0; i < arr.length; i++) arr[i] = -arr[i];
  for (let tri = 0; tri < arr.length; tri += 9) {
    for (let c = 0; c < 3; c++) {
      const b = tri + 3 + c;
      const cIdx = tri + 6 + c;
      const tmp = arr[b];
      arr[b] = arr[cIdx];
      arr[cIdx] = tmp;
    }
  }
  pos.needsUpdate = true;

  const merged = mergeGeometries([first, second]);
  first.dispose();
  second.dispose();
  return merged;
}

// Small-stellated-dodecahedron look: a pyramid ("spike") raised on each
// pentagonal face of a dodecahedron. DodecahedronGeometry(r, 0) is 36 loose
// triangles (12 pentagons fanned into 3 each, reusing only the 5 corner
// vertices), so triangles are clustered by rounded face normal, each
// cluster's 5 unique corners are ordered by angle around the face centroid,
// and 5 outward-wound triangles connect the ring to an apex pushed out along
// the face normal.
function buildStellatedDodecahedron(): THREE.BufferGeometry {
  const spikeHeight = R * 0.9;
  const base = positionsOnly(new THREE.DodecahedronGeometry(R, 0));
  const arr = base.getAttribute('position').array as Float32Array;

  // Cluster triangles into pentagonal faces by (rounded) face normal. The
  // `+ 0` folds IEEE negative zero into positive zero so a component that
  // computes as -1e-8 on one triangle and +1e-8 on another keys identically.
  const q = (x: number): number => Math.round(x * 100) / 100 + 0;
  const clusters = new Map<string, number[]>(); // key → triangle start offsets
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const n = new THREE.Vector3();
  for (let tri = 0; tri < arr.length; tri += 9) {
    a.fromArray(arr, tri);
    b.fromArray(arr, tri + 3);
    c.fromArray(arr, tri + 6);
    n.subVectors(b, a).cross(c.clone().sub(a)).normalize();
    const key = `${q(n.x)},${q(n.y)},${q(n.z)}`;
    const list = clusters.get(key);
    if (list) list.push(tri);
    else clusters.set(key, [tri]);
  }

  const spikeVerts: number[] = [];
  for (const tris of clusters.values()) {
    // Unique corner vertices of this pentagon (dedupe by rounded position).
    const seen = new Set<string>();
    const ring: THREE.Vector3[] = [];
    for (const tri of tris) {
      for (let v = 0; v < 3; v++) {
        const p = new THREE.Vector3().fromArray(arr, tri + v * 3);
        const key = `${p.x.toFixed(4)},${p.y.toFixed(4)},${p.z.toFixed(4)}`;
        if (!seen.has(key)) {
          seen.add(key);
          ring.push(p);
        }
      }
    }

    const centroid = new THREE.Vector3();
    for (const p of ring) centroid.add(p);
    centroid.divideScalar(ring.length);
    // Dodecahedron is origin-centered, so the face normal is radial.
    const normal = centroid.clone().normalize();

    // Order the ring counter-clockwise as seen from outside: angles in the
    // (u, v = normal × u) basis increase CCW when viewed from the normal side.
    const u = ring[0].clone().sub(centroid).normalize();
    const v = new THREE.Vector3().crossVectors(normal, u);
    const angleOf = (p: THREE.Vector3): number => {
      const d = p.clone().sub(centroid);
      return Math.atan2(d.dot(v), d.dot(u));
    };
    ring.sort((p, q) => angleOf(p) - angleOf(q));

    // (ring[i], ring[i+1], apex) with a CCW ring gives outward-facing spikes.
    const apex = centroid.clone().addScaledVector(normal, spikeHeight);
    for (let i = 0; i < ring.length; i++) {
      const p0 = ring[i];
      const p1 = ring[(i + 1) % ring.length];
      spikeVerts.push(p0.x, p0.y, p0.z, p1.x, p1.y, p1.z, apex.x, apex.y, apex.z);
    }
  }

  const spikes = new THREE.BufferGeometry();
  spikes.setAttribute('position', new THREE.Float32BufferAttribute(spikeVerts, 3));

  const merged = mergeGeometries([base, spikes]);
  base.dispose();
  spikes.dispose();
  return merged;
}
